import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play } from 'lucide-react';
import type { ApiVideoProvider } from '@/api/types';
import { HlsPlayer } from './HlsPlayer';
import type { NextLesson } from './PlayerControls';

interface Props {
  provider: ApiVideoProvider;
  videoRef: string;
  title: string;
  courseSlug: string;
  lessonSlug: string;
  /** HLS: video tugaganda "Keyingi dars" */
  next?: NextLesson;
}

/**
 * Dars videosi. Avval faqat muqova — bosilganda player yuklanadi: sahifa tez ochiladi,
 * prerender HTML da video manzili yo'q.
 * - YOUTUBE: youtube-nocookie iframe (bosilmaguncha YouTube cookie o'rnatmaydi). Video unlisted —
 *   ID faqat darsga kirish huquqi borlarga API dan keladi.
 * - HLS: o'z serverimizdagi shifrlangan video — HlsPlayer imzolangan linkni bosilgandan keyin so'raydi.
 */
export function LessonVideo({ provider, videoRef, title, courseSlug, lessonSlug, next }: Props) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);

  if (provider !== 'YOUTUBE' && provider !== 'HLS') return null;

  if (playing && provider === 'HLS') {
    return <HlsPlayer courseSlug={courseSlug} lessonSlug={lessonSlug} title={title} autoPlay next={next} />;
  }

  const params = new URLSearchParams({ autoplay: '1', rel: '0', modestbranding: '1', playsinline: '1' });

  return (
    <div className="relative aspect-video w-full overflow-hidden rounded-lg border border-line bg-black">
      {playing ? (
        <iframe
          className="absolute inset-0 h-full w-full"
          src={`https://www.youtube-nocookie.com/embed/${encodeURIComponent(videoRef)}?${params}`}
          title={title}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <button
          type="button"
          onClick={() => setPlaying(true)}
          className="group absolute inset-0 flex h-full w-full items-center justify-center"
          aria-label={`${t('courses.videoTitle')}: ${title}`}
        >
          {provider === 'YOUTUBE' ? (
            <img
              src={`https://i.ytimg.com/vi/${encodeURIComponent(videoRef)}/hqdefault.jpg`}
              alt=""
              loading="lazy"
              className="absolute inset-0 h-full w-full object-cover opacity-80 transition-opacity group-hover:opacity-100"
            />
          ) : (
            <span
              aria-hidden
              className="absolute inset-0 bg-gradient-to-br from-brand-900 via-black to-black opacity-90 transition-opacity group-hover:opacity-100"
            />
          )}
          <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-brand-600 text-white shadow-lg transition-transform group-hover:scale-110">
            <Play className="ml-1 h-7 w-7" fill="currentColor" />
          </span>
          {provider === 'HLS' && (
            <span className="absolute inset-x-0 bottom-0 truncate px-4 pb-3 text-left text-sm font-medium text-white/85">
              {title}
            </span>
          )}
        </button>
      )}
    </div>
  );
}
